import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Shield, CheckCircle } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

export default function Setup2FA() {
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIs2FAEnabled(user.totp_enabled || false);

    if (!user.totp_enabled) {
      fetchQRCode();
    }
  }, []);

  const fetchQRCode = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/2fa-setup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
    } catch (error) {
      toast.error('Failed to load 2FA setup');
    }
  };

  const handleEnable2FA = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('code', verifyCode);

      await axios.post(`${API}/auth/enable-2fa`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.totp_enabled = true;
      localStorage.setItem('user', JSON.stringify(user));

      setIs2FAEnabled(true);
      toast.success('2FA enabled successfully!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code. Please try again.');
      setVerifyCode('');
    } finally {
      setLoading(false);
    }
  };

  if (is2FAEnabled) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto" data-testid="2fa-enabled-page">
          <Card className="p-8 border border-slate-200 rounded-xl shadow-sm text-center space-y-6">
            <div className="flex justify-center">
              <div className="p-4 bg-green-100 rounded-full">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                2FA is Enabled
              </h2>
              <p className="text-slate-600">
                Your account is protected with Two-Factor Authentication
              </p>
            </div>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              Go to Dashboard
            </Button>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid="setup-2fa-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">
            Set Up Two-Factor Authentication
          </h1>
          <p className="text-base text-slate-600 mt-2">
            Secure your account with Google Authenticator
          </p>
        </div>

        <div className="space-y-6">
          <Card className="p-8 border border-slate-200 rounded-xl shadow-sm">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-teal-600" />
                  Step 1: Scan QR Code
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Open Google Authenticator app and scan this QR code
                </p>

                {qrCode && (
                  <div className="flex justify-center bg-white p-6 rounded-lg border-2 border-slate-200">
                    <img src={qrCode} alt="2FA QR Code" className="w-64 h-64" />
                  </div>
                )}

                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Secret Key (if you can't scan):</p>
                  <code className="text-sm text-slate-900 font-mono break-all">{secret}</code>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">
                  Step 2: Verify Code
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Enter the 6-digit code from Google Authenticator
                </p>

                <form onSubmit={handleEnable2FA} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="verify-code">6-Digit Code</Label>
                    <Input
                      id="verify-code"
                      data-testid="verify-code-input"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="000000"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                      required
                      className="h-12 text-center text-2xl tracking-widest max-w-xs"
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      data-testid="enable-2fa-button"
                      type="submit"
                      className="bg-slate-900 hover:bg-slate-800 text-white"
                      disabled={loading || verifyCode.length !== 6}
                    >
                      {loading ? 'Verifying...' : 'Enable 2FA'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => navigate('/dashboard')}
                      className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    >
                      Skip for Now
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </Card>

          <Card className="p-6 border border-blue-200 bg-blue-50">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Why enable 2FA?</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Adds an extra layer of security to your account</li>
              <li>• Protects against unauthorized access</li>
              <li>• Required for approvers to manage expense approvals</li>
            </ul>
          </Card>
        </div>
      </div>
    </Layout>
  );
}